import { forwardRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Button, ButtonProps } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

interface RefreshIconButtonProps extends Omit<ButtonProps, 'children'> {
  tooltip?: string;
  showTooltip?: boolean;
}

/**
 * Standardized refresh icon button with primary color on hover.
 */
export const RefreshIconButton = forwardRef<HTMLButtonElement, RefreshIconButtonProps>(
  ({ tooltip, showTooltip = true, className, ...props }, ref) => {
    const { t } = useTranslation('common');
    const label = tooltip || t('app.refresh');

    const button = (
      <Button
        ref={ref}
        variant="ghost"
        size="icon"
        aria-label={label}
        className={cn('hover:bg-primary/10 hover:text-primary', className)}
        {...props}
      >
        <RefreshCw className="h-4 w-4" />
      </Button>
    );

    if (!showTooltip) return button;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  }
);

RefreshIconButton.displayName = 'RefreshIconButton';
